import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";
import { ApiError, api, apiClient } from "../../lib/api";
import type { AuthSnapshot } from "../../lib/types";

interface AuthValue {
  snapshot: AuthSnapshot | null;
  loading: boolean;
  unavailable: boolean;
  setSnapshot: (snapshot: AuthSnapshot | null) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [snapshot, setSnapshotState] = useState<AuthSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  const setSnapshot = useCallback((next: AuthSnapshot | null) => {
    setSnapshotState(next);
    if (next?.csrfToken) apiClient.setCsrfToken(next.csrfToken);
    else apiClient.clearCsrfToken();
  }, []);

  const refresh = useCallback(async () => {
    setUnavailable(false);
    try {
      const next = await api.auth.me();
      setSnapshot(next);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setSnapshot(null);
      else setUnavailable(true);
    } finally {
      setLoading(false);
    }
  }, [setSnapshot]);

  useEffect(() => {
    let cancelled = false;
    api.auth.me().then((next) => {
      if (!cancelled) setSnapshot(next);
    }).catch((error: unknown) => {
      if (cancelled) return;
      if (error instanceof ApiError && error.status === 401) setSnapshot(null);
      else setUnavailable(true);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [setSnapshot]);

  const logout = useCallback(async () => {
    try { await api.auth.logout(); } finally { setSnapshot(null); }
  }, [setSnapshot]);

  const value = useMemo(() => ({ snapshot, loading, unavailable, setSnapshot, refresh, logout }), [snapshot, loading, unavailable, setSnapshot, refresh, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
